import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/cn';

const alertVariants = cva(
  'relative w-full rounded border p-4 text-sm [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:h-4 [&>svg]:w-4 [&>svg+div]:pl-7 [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default:
          'border-[var(--color-hairline-strong)] bg-[var(--color-paper-dim)] text-[var(--color-ink)] [&>svg]:text-[var(--color-ink-muted)]',
        info: 'border-[var(--color-primary)] bg-[var(--color-primary-50)] text-[var(--color-primary-700)] [&>svg]:text-[var(--color-primary)]',
        warning:
          'border-[var(--color-ochre)] bg-[var(--color-ochre-50)] text-[color-mix(in_oklch,var(--color-ochre)_60%,var(--color-ink))] [&>svg]:text-[var(--color-ochre)]',
        danger:
          'border-[var(--color-danger)] bg-[var(--color-danger-50)] text-[var(--color-danger)] [&>svg]:text-[var(--color-danger)]',
        accent:
          'border-[var(--color-accent)] bg-[var(--color-accent-50)] text-[var(--color-accent-600)] [&>svg]:text-[var(--color-accent)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  ),
);
Alert.displayName = 'Alert';

export const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';
